import {
    doc,
    getDoc,
    setDoc,
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    orderBy,
    query,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Website content operations
export const getSiteData = async () => {
    try {
        const docRef = doc(db, 'website', 'content');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            // Return default data if no document exists
            return getDefaultSiteData();
        }
    } catch (error) {
        console.error('Error fetching site data:', error);
        return getDefaultSiteData();
    }
};

export const updateSiteData = async (section, sectionData) => {
    try {
        console.log('🔥 dataService updateSiteData:', { section, sectionData });
        const docRef = doc(db, 'website', 'content');
        await setDoc(docRef, {
            [section]: {
                ...sectionData,
                lastUpdated: serverTimestamp()
            }
        }, { merge: true });
        console.log('🔥 Firebase document updated successfully');
    } catch (error) {
        console.error(`🔥 Error updating ${section} data:`, error);
        throw error;
    }
};

// Blog operations
export const getBlogPosts = async () => {
    try {
        const blogRef = collection(db, 'blog');
        const q = query(blogRef, orderBy('date', 'desc'));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching blog posts:', error);
        return [];
    }
};

export const addBlogPost = async (postData) => {
    try {
        const blogRef = collection(db, 'blog');
        const docRef = await addDoc(blogRef, {
            ...postData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });
        return docRef.id;
    } catch (error) {
        console.error('Error adding blog post:', error);
        throw error;
    }
};

export const updateBlogPost = async (postId, postData) => {
    try {
        const docRef = doc(db, 'blog', postId);
        await updateDoc(docRef, {
            ...postData,
            updatedAt: serverTimestamp()
        });
        console.log('Blog post updated successfully');
    } catch (error) {
        console.error('Error updating blog post:', error);
        throw error;
    }
};

export const deleteBlogPost = async (postId) => {
    try {
        const docRef = doc(db, 'blog', postId);
        await deleteDoc(docRef);
        console.log('Blog post deleted successfully');
    } catch (error) {
        console.error('Error deleting blog post:', error);
        throw error;
    }
};

// Services operations - Updated to work with array in website/content document
export const getServices = async () => {
    try {
        const docRef = doc(db, 'website', 'content');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists() && docSnap.data().services) {
            return docSnap.data().services;
        }
        return [];
    } catch (error) {
        console.error('Error fetching services:', error);
        return [];
    }
};

export const addService = async (serviceData) => {
    try {
        const docRef = doc(db, 'website', 'content');
        const docSnap = await getDoc(docRef);

        const currentServices = docSnap.exists() && docSnap.data().services ? docSnap.data().services : [];

        // Generate a unique ID for the new service
        const newServiceId = Date.now().toString();
        const newService = {
            ...serviceData,
            id: newServiceId,
            createdAt: new Date().toISOString()
        };

        await setDoc(docRef, {
            services: [...currentServices, newService]
        }, { merge: true });

        return newServiceId;
    } catch (error) {
        console.error('Error adding service:', error);
        throw error;
    }
};

export const updateService = async (serviceId, serviceData) => {
    try {
        const docRef = doc(db, 'website', 'content');
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists() || !docSnap.data().services) {
            throw new Error('Services not found');
        }

        const currentServices = docSnap.data().services;
        // Ensure string comparison for mapping
        const serviceIdString = String(serviceId);
        const updatedServices = currentServices.map(service =>
            String(service.id) === serviceIdString ? { ...serviceData, id: serviceIdString } : service
        );

        await setDoc(docRef, {
            services: updatedServices
        }, { merge: true });

        console.log('Service updated successfully');
    } catch (error) {
        console.error('Error updating service:', error);
        throw error;
    }
};

export const deleteService = async (serviceId) => {
    try {
        const docRef = doc(db, 'website', 'content');
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists() || !docSnap.data().services) {
            throw new Error('Services not found');
        }

        const currentServices = docSnap.data().services;
        // Ensure string comparison for filtering
        const serviceIdString = String(serviceId);
        const updatedServices = currentServices.filter(service =>
            String(service.id) !== serviceIdString
        );

        await setDoc(docRef, {
            services: updatedServices
        }, { merge: true });

        console.log('Service deleted successfully');
    } catch (error) {
        console.error('Error deleting service:', error);
        throw error;
    }
};

// Testimonials operations
export const getTestimonials = async () => {
    try {
        const testimonialsRef = collection(db, 'testimonials');
        const querySnapshot = await getDocs(testimonialsRef);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching testimonials:', error);
        return [];
    }
};

export const addTestimonial = async (testimonialData) => {
    try {
        const testimonialsRef = collection(db, 'testimonials');
        const docRef = await addDoc(testimonialsRef, {
            ...testimonialData,
            createdAt: serverTimestamp()
        });
        return docRef.id;
    } catch (error) {
        console.error('Error adding testimonial:', error);
        throw error;
    }
};

export const updateTestimonial = async (testimonialId, testimonialData) => {
    try {
        const docRef = doc(db, 'testimonials', testimonialId);
        await updateDoc(docRef, testimonialData);
        console.log('Testimonial updated successfully');
    } catch (error) {
        console.error('Error updating testimonial:', error);
        throw error;
    }
};

export const deleteTestimonial = async (testimonialId) => {
    try {
        const docRef = doc(db, 'testimonials', testimonialId);
        await deleteDoc(docRef);
        console.log('Testimonial deleted successfully');
    } catch (error) {
        console.error('Error deleting testimonial:', error);
        throw error;
    }
};

// Resources operations
export const getResources = async () => {
    try {
        const resourcesRef = collection(db, 'resources');
        const querySnapshot = await getDocs(resourcesRef);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching resources:', error);
        return [];
    }
};

export const addResource = async (resourceData) => {
    try {
        const resourcesRef = collection(db, 'resources');
        const docRef = await addDoc(resourcesRef, {
            ...resourceData,
            createdAt: serverTimestamp()
        });
        return docRef.id;
    } catch (error) {
        console.error('Error adding resource:', error);
        throw error;
    }
};

export const updateResource = async (resourceId, resourceData) => {
    try {
        const docRef = doc(db, 'resources', resourceId);
        await updateDoc(docRef, resourceData);
        console.log('Resource updated successfully');
    } catch (error) {
        console.error('Error updating resource:', error);
        throw error;
    }
};

export const deleteResource = async (resourceId) => {
    try {
        const docRef = doc(db, 'resources', resourceId);
        await deleteDoc(docRef);
        console.log('Resource deleted successfully');
    } catch (error) {
        console.error('Error deleting resource:', error);
        throw error;
    }
};

// Default data function
const getDefaultSiteData = () => ({
    hero: {
        title: "Welcome to Your Healing Journey",
        subtitle: "Professional therapy services to help you navigate life's challenges with compassion and expertise.",
        buttonText: "Schedule Consultation"
    },
    about: {
        title: "About My Practice",
        content: "With over 10 years of experience in mental health counseling, I provide a safe, supportive environment for individuals seeking positive change.",
        image: "/api/placeholder/400/500"
    },
    contact: {
        phone: "(555) 123-4567",
        email: "contact@therapist.com",
        address: "123 Wellness Street, City, State 12345",
        hours: "Monday - Friday: 9AM - 6PM"
    },
    services: []
});